import { motion } from "framer-motion";
import { Map, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const Stat = ({ label, value, tone = "text-white", testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className={`text-2xl sm:text-3xl font-semibold tabular-nums ${tone}`}>{value ?? "—"}</div>
    <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
  </div>
);

export const MarketHeader = ({ stats, job, onExpand }) => {
  const running = job?.status === "running";
  const empty = !stats || stats.total === 0;
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="glass-card p-5 sm:p-6 mb-6 relative overflow-hidden" data-testid="market-header">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div className="p-3 rounded-xl bg-ism-teal/15 border border-ism-teal/25 flex-shrink-0"><Map className="h-6 w-6 text-ism-teal" /></div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl text-white tracking-tight">Your <span className="text-ism-teal">Market</span></h1>
            <p className="text-sm text-gray-400 mt-1 max-w-xl">
              Every firm here was named by the model, found in a web list or looks like one of your customers — then verified in the company database and gated on your size and industry.
              Hunts draw the next untouched firms from this map, one buyer per company.
            </p>
          </div>
        </div>
        <Button onClick={onExpand} disabled={running} className="bg-ism-teal hover:bg-ism-teal/90 text-black rounded-xl flex-shrink-0" data-testid="market-expand-btn">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {running ? "Mapping…" : empty ? "Map my market" : "Expand market"}
        </Button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 sm:gap-6 mt-6">
        <Stat label="Mapped" value={stats?.total} testId="market-stat-total" />
        <Stat label="Untouched" value={stats?.untouched} tone="text-ism-teal" testId="market-stat-untouched" />
        <Stat label="Hunted" value={stats?.hunted} testId="market-stat-hunted" />
        <Stat label="Parked" value={stats?.parked} tone="text-amber-300" testId="market-stat-parked" />
        <Stat label="Not a fit" value={stats?.not_fit} tone="text-gray-400" testId="market-stat-notfit" />
        <Stat label="Unverified / pending" value={stats ? `${stats.unverified || 0} / ${stats.pending || 0}` : undefined} tone="text-gray-500" testId="market-stat-unverified" />
      </div>
      {running && (
        <div className="mt-5" data-testid="market-job-progress">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5"><span>{job.stage}</span><span>{job.progress}%</span></div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-ism-teal transition-[width] duration-700" style={{ width: `${job.progress}%` }} /></div>
        </div>
      )}
      {!running && stats?.pending > 0 && (
        <p className="mt-4 text-xs text-gray-500" data-testid="market-pending-note">{stats.pending} firms are queued for verification — the next expansion checks them first.</p>
      )}
      {!running && stats?.untouched < stats?.growBelow && stats?.total > 0 && (
        <p className="mt-4 text-xs text-amber-300/90" data-testid="market-low-warning">
          Fewer than {stats.growBelow} untouched firms — the agent will extend the map in the background after your next hunt.
        </p>
      )}
    </motion.div>
  );
};
